import React, { FC } from 'react';
import {
  FieldValues,
  Control,
  UseFormSetValue,
  useWatch,
  FieldErrors,
} from 'react-hook-form';
import { FormQuestion, FormQuestionType } from 'api/form/types';
import { AttachmentField } from 'components/molecules/forms/form-fields/attachment-field';
import { CheckBoxGridField } from 'components/molecules/forms/form-fields/check-box-grid-field';
import { CheckboxChoiceField } from 'components/molecules/forms/form-fields/checkbox-choice-field';
import { CurrencyField } from 'components/molecules/forms/form-fields/currency-field';
import { DateField } from 'components/molecules/forms/form-fields/date-field';
import { DropdownChoiceField } from 'components/molecules/forms/form-fields/dropdown-choice-field';
import { DropdownMultiChoiceField } from 'components/molecules/forms/form-fields/dropdown-multi-choice-field';
import { EmailField } from 'components/molecules/forms/form-fields/email-field';
import { LargeTextField } from 'components/molecules/forms/form-fields/large-text-field';
import { MultiChoiceGridField } from 'components/molecules/forms/form-fields/multi-choice-grid-field';
import { NumberField } from 'components/molecules/forms/form-fields/number-field';
import { PhoneField } from 'components/molecules/forms/form-fields/phone-field';
import { RadioChoiceField } from 'components/molecules/forms/form-fields/radio-choice-field';
import { RatingField } from 'components/molecules/forms/form-fields/rating-field';
import { SmallTextField } from 'components/molecules/forms/form-fields/small-text-field';
import { StatementField } from 'components/molecules/forms/form-fields/statement-field';
import { ChoiceQuestionType } from 'components/organisms/templates/forms/form-builder/constants';
import { getTextFromHTML } from 'utils/getTextFromHTML';

export type Renderer = React.FunctionComponent<any> | null;
export const computeFieldRenderer = (type: FormQuestionType): Renderer => {
  switch (type) {
    case FormQuestionType.Phone:
      return PhoneField;
    case FormQuestionType.Email:
      return EmailField;
    case FormQuestionType.TextShort:
      return SmallTextField;
    case FormQuestionType.TextLong:
      return LargeTextField;
    case FormQuestionType.ChoiceDropdown:
      return DropdownChoiceField;
    case FormQuestionType.ChoiceDropdownMultiselect:
      return DropdownMultiChoiceField;
    case FormQuestionType.ChoiceRadio:
      return RadioChoiceField;
    case FormQuestionType.ChoiceCheckbox:
      return CheckboxChoiceField;
    case FormQuestionType.TextStatement:
      return StatementField;
    case FormQuestionType.CheckBoxGrid:
      return CheckBoxGridField;
    case FormQuestionType.MultipleChoiceGrid:
      return MultiChoiceGridField;
    case FormQuestionType.Currency:
      return CurrencyField;
    case FormQuestionType.StarRating:
      return RatingField;
    case FormQuestionType.Date:
      return DateField;
    case FormQuestionType.Number:
      return NumberField;
    case FormQuestionType.Attachment:
      return AttachmentField;
    default:
      return null;
  }
};

const SingleOptionBranchRenderer = ({
  question,
  control,
  setValue,
  value,
  errors,
  disabled,
  renderRowErrorObject,
}: {
  question: FormQuestion;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  value: number | undefined;
  errors?: FieldErrors<FieldValues>;
  disabled: boolean;
  renderRowErrorObject?: { [key: number]: boolean };
}) => {
  const selectedOption = question.options?.find(
    (option) => option.optionId === value,
  );

  return (
    <>
      {selectedOption && selectedOption.branches?.length
        ? selectedOption.branches.map((branchQuestion) => {
            return (
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              <FormQuestionRenderer
                question={branchQuestion}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={disabled}
                renderRowErrorObject={renderRowErrorObject}
              />
            );
          })
        : ''}
    </>
  );
};

const MultiOptionBranchRenderer = ({
  question,
  control,
  setValue,
  value,
  errors,
  disabled,
  renderRowErrorObject,
}: {
  question: FormQuestion;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  value: number[] | undefined;
  errors?: FieldErrors<FieldValues>;
  disabled: boolean;
  renderRowErrorObject?: { [key: number]: boolean };
}) => {
  return (
    <>
      {question.options?.map((option) => {
        if (
          (value || []).includes(option.optionId) &&
          option.branches?.length
        ) {
          return option.branches.map((branchQuestion) => {
            return (
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              <FormQuestionRenderer
                question={branchQuestion}
                control={control}
                setValue={setValue}
                errors={errors}
                disabled={disabled}
                renderRowErrorObject={renderRowErrorObject}
              />
            );
          });
        }

        return '';
      })}
    </>
  );
};

const BranchingQuestionRenderer = ({
  question,
  control,
  setValue,
  errors,
  disabled,
  renderRowErrorObject,
}: {
  question: FormQuestion;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors?: FieldErrors<FieldValues>;
  disabled: boolean;
  renderRowErrorObject?: { [key: number]: boolean };
}) => {
  const value = useWatch({
    name: question.questionId.toString(),
    control,
  });
  const isSingleOptionBranch = [
    FormQuestionType.ChoiceRadio,
    FormQuestionType.ChoiceDropdown,
  ].includes(question.formQuestionType);

  const isMultiOptionBranch = [
    FormQuestionType.ChoiceCheckbox,
    FormQuestionType.ChoiceDropdownMultiselect,
  ].includes(question.formQuestionType);

  return (
    <>
      {isSingleOptionBranch ? (
        <SingleOptionBranchRenderer
          question={question}
          control={control}
          setValue={setValue}
          value={value}
          errors={errors}
          disabled={disabled}
          renderRowErrorObject={renderRowErrorObject}
        />
      ) : (
        ''
      )}
      {isMultiOptionBranch ? (
        <MultiOptionBranchRenderer
          question={question}
          control={control}
          setValue={setValue}
          value={value}
          errors={errors}
          disabled={disabled}
          renderRowErrorObject={renderRowErrorObject}
        />
      ) : (
        ''
      )}
    </>
  );
};

export const FormQuestionRenderer: FC<{
  question: FormQuestion;
  control: Control<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  errors?: FieldErrors<FieldValues>;
  disabled: boolean;
  renderRowErrorObject?: { [key: number]: boolean };
}> = ({
  question,
  control,
  setValue,
  errors,
  disabled,
  renderRowErrorObject,
}): JSX.Element => {
  const FieldRenderer = computeFieldRenderer(
    question.formQuestionType,
  ) as Renderer;

  const isChoiceQuestion = ChoiceQuestionType.includes(
    question.formQuestionType,
  );

  return (
    <>
      {FieldRenderer ? (
        <section data-cy={getTextFromHTML(question.questionText)}>
          <FieldRenderer
            field={question}
            setValue={setValue}
            errors={errors}
            control={control}
            disabled={disabled}
            renderRowErrorObject={renderRowErrorObject}
          />
        </section>
      ) : null}
      {isChoiceQuestion ? (
        <BranchingQuestionRenderer
          question={question}
          control={control}
          setValue={setValue}
          errors={errors}
          disabled={disabled}
          renderRowErrorObject={renderRowErrorObject}
        />
      ) : null}
    </>
  );
};
